/**
 * DialerSettingsContext — Phase 4 etape 5.
 *
 * Centralise TOUS les settings persistés serveur (caller.dialer_settings) :
 * - Etat React (useState pour chaque setting)
 * - Hydration au mount depuis callerData.dialer_settings
 * - Debounce-save vers /auth/me via api.functions.invoke('updateCallerSession')
 * - Helper cycleTheme
 *
 * Voir ADR-008 chemin de sortie + docs/refactor/refs-audit.md.
 *
 * Compat : le DialerProvider consomme useDialerSettings() en interne et
 * re-expose toutes les valeurs via la façade useDialer(). Les composants
 * existants ne bougent pas.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
// #23 -- passerelle du reglage son vers les emetteurs hors React.
import { definirSonActif } from '@/state/soundPrefs';
// @ts-ignore — JS module
import { api } from '@/api/api-client';
import { useAuthStore } from '@/stores/authStore';
import { errorCodeOf } from '@/services/errorMessage';
import i18n from 'i18next';
import { toast } from 'sonner';

const THEMES = ['dark', 'sober', 'soft'] as const;
type Theme = (typeof THEMES)[number];
type Language = 'fr' | 'en';
type VoicemailMode = 'off' | 'recorded';

interface ReminderChannels {
  emailJ1: boolean;
  emailH1: boolean;
  smsJ1: boolean;
  smsH1: boolean;
}

interface HubspotSyncConfig {
  tasks: boolean;
  recordings: boolean;
}

interface CallerDataLite {
  id?: string;
  caller_email?: string;
  dialer_settings?: Record<string, any> | null;
}

export interface DialerSettingsValue {
  // Core UI
  language: Language;
  setLanguage: (v: Language) => void;
  theme: Theme;
  setTheme: (v: Theme) => void;
  darkMode: boolean;
  cycleTheme: () => void;

  soundEnabled: boolean;
  setSoundEnabled: (v: boolean) => void;

  // Dialer behavior
  powerMode: boolean;
  setPowerMode: (v: boolean) => void;
  parallelCallsCount: number;
  setParallelCallsCount: (v: number) => void;
  callerIdRotation: string;
  setCallerIdRotation: (v: string) => void;
  ringTimeout: number;
  setRingTimeout: (v: number) => void;
  maxCallAttempts: string;
  setMaxCallAttempts: (v: string) => void;
  maxCallAttemptsPeriod: string;
  setMaxCallAttemptsPeriod: (v: string) => void;
  powerModePauseBetweenBatches: number;
  setPowerModePauseBetweenBatches: (v: number) => void;
  completeTaskOnDial: boolean;
  setCompleteTaskOnDial: (v: boolean) => void;

  // Voicemail
  powerModeLeaveVoicemail: boolean;
  setPowerModeLeaveVoicemail: (v: boolean) => void;
  voicemailAudioUrl: string;
  setVoicemailAudioUrl: (v: string) => void;
  voicemailMode: VoicemailMode;
  setVoicemailMode: (mode: VoicemailMode) => void;

  // View / UI
  kanbanEnabled: boolean;
  setKanbanEnabled: (v: boolean) => void;
  viewMode: string;
  setViewMode: (v: string) => void;
  navTabOrder: any;
  setNavTabOrder: (v: any) => void;
  columnConfig: any;
  setColumnConfig: (v: any) => void;
  selectedMicrophone: string;
  setSelectedMicrophone: (v: string) => void;

  // CRM
  crmProvider: string;
  setCrmProvider: (v: string) => void;
  hubspotSyncConfig: HubspotSyncConfig;
  setHubspotSyncConfig: (v: HubspotSyncConfig) => void;
  hubspotAutoSyncTasks: boolean;
  setHubspotAutoSyncTasks: (v: boolean) => void;
  hubspotAutoSyncRecordings: boolean;
  setHubspotAutoSyncRecordings: (v: boolean) => void;
  hubspotLastSyncTasks: Date | null;
  setHubspotLastSyncTasks: (v: Date | null) => void;
  hubspotLastSyncRecordings: Date | null;
  setHubspotLastSyncRecordings: (v: Date | null) => void;

  // Power Hour
  powerHourGoal: number;
  /** Stop Power Mode after this many connected calls (0: off). */
  stopAfterConnected: number;
  setStopAfterConnected: (n: number) => void;
  /** Personal numbers the agent declared as outgoing numbers. */
  personalCallerIds: string[];
  setPersonalCallerIds: (ids: string[]) => void;
  setPowerHourGoal: (v: number) => void;
  powerHourDuration: number;
  setPowerHourDuration: (v: number) => void;

  // Smart queue
  smartQueueEnabled: boolean;
  setSmartQueueEnabled: (v: boolean) => void;

  // Markets / territories
  territories: string[];
  setTerritories: (v: string[]) => void;
  activeMarkets: string[];
  setActiveMarkets: (v: string[]) => void;
  conversationThreshold: number;
  setConversationThreshold: (v: number) => void;
  conversationOutcomes: any;
  setConversationOutcomes: (v: any) => void;

  // Reminders
  remindersEnabled: boolean;
  setRemindersEnabled: (v: boolean) => void;
  reminderChannels: ReminderChannels;
  setReminderChannels: (v: ReminderChannels) => void;

  // AI Coach
  aiCoachEnabledPowerMode: boolean;
  setAiCoachEnabledPowerMode: (v: boolean) => void;
  aiCoachEnabledManualCall: boolean;
  setAiCoachEnabledManualCall: (v: boolean) => void;

  // Result of the last server save of the settings (RUI-09). `seq` grows on
  // every response so a screen can react to each one, success or failure.
  settingsSave: { seq: number; ok: boolean; issuedAt: number; code?: string } | null;
  retrySettingsSave: () => void;
}

const DialerSettingsContext = createContext<DialerSettingsValue | null>(null);

interface ProviderProps {
  callerData: CallerDataLite | null;
  children: React.ReactNode;
}

export function DialerSettingsProvider({ callerData, children }: ProviderProps) {
  // ── States ─────────────────────────────────────────────────────────────────
  const [language, setLanguage] = useState<Language>(() => {
    const detected = (i18n.language || '').slice(0, 2);
    return (detected === 'en' || detected === 'fr') ? (detected as Language) : 'fr';
  });
  const [theme, setTheme] = useState<Theme>('dark');
  const darkMode = theme === 'dark';
  const [soundEnabled, setSoundEnabled] = useState(true);

  // #23 -- on depose `soundEnabled` dans une passerelle sans React, pour que
  // les emetteurs de son qui n'ont pas acces au contexte puissent le lire
  // (playRingDing, dans TwilioDeviceContext, sonnait sans aucun garde).
  useEffect(() => { definirSonActif(soundEnabled); }, [soundEnabled]);
  const [powerMode, setPowerMode] = useState(false);
  const [parallelCallsCount, setParallelCallsCount] = useState(3);
  const [callerIdRotation, setCallerIdRotation] = useState('manual');
  const [ringTimeout, setRingTimeout] = useState(30);
  const [maxCallAttempts, setMaxCallAttempts] = useState('unlimited');
  const [maxCallAttemptsPeriod, setMaxCallAttemptsPeriod] = useState('week');
  const [powerModeLeaveVoicemail, setPowerModeLeaveVoicemail] = useState(false);
  const [voicemailAudioUrl, setVoicemailAudioUrl] = useState('');
  const voicemailMode: VoicemailMode = (powerModeLeaveVoicemail && voicemailAudioUrl) ? 'recorded' : 'off';
  const setVoicemailMode = (mode: VoicemailMode) => {
    setPowerModeLeaveVoicemail(mode === 'recorded');
  };
  const [completeTaskOnDial, setCompleteTaskOnDial] = useState(false);
  const [powerModePauseBetweenBatches, setPowerModePauseBetweenBatches] = useState(0);
  const [kanbanEnabled, setKanbanEnabled] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [navTabOrder, setNavTabOrder] = useState<any>(null);
  const [columnConfig, setColumnConfig] = useState<any>(null);
  const [crmProvider, setCrmProvider] = useState('hubspot');
  const [hubspotSyncConfig, setHubspotSyncConfig] = useState<HubspotSyncConfig>({ tasks: true, recordings: true });
  const [selectedMicrophone, setSelectedMicrophone] = useState('');
  const [powerHourGoal, setPowerHourGoal] = useState(50);
  // Both used to live in each browser's localStorage, so they differed from
  // one browser to another. They are dialer settings now; the old local
  // value seeds the state once and is then removed.
  const [stopAfterConnected, setStopAfterConnected] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('xdial-stop-after-connected')) || 0;
      localStorage.removeItem('xdial-stop-after-connected');
      return v;
    } catch { return 0; }
  });
  const [personalCallerIds, setPersonalCallerIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('xdial-personal-caller-ids');
      localStorage.removeItem('xdial-personal-caller-ids');
      const v = raw ? JSON.parse(raw) : [];
      return Array.isArray(v) ? v : [];
    } catch { return []; }
  });
  const [powerHourDuration, setPowerHourDuration] = useState(60);
  const [smartQueueEnabled, setSmartQueueEnabled] = useState(false);
  const [hubspotAutoSyncTasks, setHubspotAutoSyncTasks] = useState(false);
  const [hubspotAutoSyncRecordings, setHubspotAutoSyncRecordings] = useState(false);
  const [hubspotLastSyncTasks, setHubspotLastSyncTasks] = useState<Date | null>(null);
  const [hubspotLastSyncRecordings, setHubspotLastSyncRecordings] = useState<Date | null>(null);
  const [territories, setTerritories] = useState<string[]>([]);
  const [activeMarkets, setActiveMarkets] = useState<string[]>(['FR']);
  const [conversationThreshold, setConversationThreshold] = useState(30);
  const [conversationOutcomes, setConversationOutcomes] = useState<any>(null);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [aiCoachEnabledPowerMode, setAiCoachEnabledPowerMode] = useState(true);
  const [aiCoachEnabledManualCall, setAiCoachEnabledManualCall] = useState(true);
  const [reminderChannels, setReminderChannels] = useState<ReminderChannels>({ emailJ1: true, emailH1: true, smsJ1: false, smsH1: false });

  // ── Server settings sync refs ──────────────────────────────────────────────
  const settingsInitRef = useRef(true);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Garde dure : hydrater une seule fois par caller pour ne pas ecraser les
  // modifs locales en cours sur un refetch /auth/me.
  const settingsHydratedForCallerRef = useRef<string | null>(null);
  const [settingsSave, setSettingsSave] = useState<{ seq: number; ok: boolean; issuedAt: number; code?: string } | null>(null);
  const lastSettingsPayloadRef = useRef<Record<string, unknown> | null>(null);
  const settingsRequestSeqRef = useRef(0);
  // QA-W4-1: the settings as the server last knew them (hydrated or saved),
  // serialised. The debounced save compares against it: a load re-hydrates
  // twice (cache, then server) and used to send the server its own settings
  // back, one PATCH /auth/update-caller per page load.
  const serverSettingsJsonRef = useRef<string | null>(null);
  const hydrationGenRef = useRef(0);

  // The "Enregistré" toast used to fire on a timer whatever the server said,
  // and a failed save was swallowed. Report the real response instead. Each
  // save has a number and a send time: a response older than one already
  // reported is dropped, and a screen can tell which change a save carried.
  const pushSettings = useCallback((settings: Record<string, unknown>) => {
    // QA-W7-3: « ouvrir en tant que » is read-only; the server would answer
    // 403 IMPERSONATION_READ_ONLY. Never send (the controls are disabled).
    if (useAuthStore.getState().impersonation) return;
    lastSettingsPayloadRef.current = settings;
    const seq = ++settingsRequestSeqRef.current;
    const issuedAt = Date.now();
    const report = (ok: boolean, code?: string) =>
      setSettingsSave(prev => (prev && prev.seq > seq ? prev : { seq, ok, issuedAt, code }));
    api.functions.invoke('updateCallerSession', { dialer_settings: settings })
      .then(() => report(true))
      .catch((err) => {
        console.warn('[settings] save failed', err);
        report(false, errorCodeOf(err) || undefined);
      });
  }, []);
  const retrySettingsSave = useCallback(() => {
    if (lastSettingsPayloadRef.current) pushSettings(lastSettingsPayloadRef.current);
  }, [pushSettings]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const cycleTheme = useCallback(() => {
    setTheme(prev => {
      const idx = THEMES.indexOf(prev);
      return THEMES[(idx + 1) % THEMES.length] ?? 'dark';
    });
  }, []);

  // ── Side effects ───────────────────────────────────────────────────────────

  // Propagate language change to i18next runtime so t() calls switch immediately.
  useEffect(() => {
    import('i18next').then(({ default: ii }) => {
      if (ii.language !== language) ii.changeLanguage(language);
    });
  }, [language]);

  // Theme management (applied on #xdial-root, not document root)
  useEffect(() => {
    const root = document.getElementById('xdial-root');
    if (!root) return;
    root.classList.remove('dark', 'sober', 'soft');
    root.classList.add(theme);
  }, [theme]);

  // Load dialer_settings from server when caller authenticates.
  // Hydrate UNE FOIS par caller — sans ça, un refetch de callerData
  // (auth refresh, StrictMode double-mount, etc) écraserait les modifs locales.
  useEffect(() => {
    if (!callerData?.dialer_settings || Object.keys(callerData.dialer_settings).length === 0) {
      settingsInitRef.current = false;
      return;
    }
    // Hydrate once from the cache, then once more when the server's copy
    // arrives. Hydrating only once per caller let a stale cached copy win:
    // the language (and every setting) flipped on every other refresh.
    const fromServer = !!(callerData as { __fromServer?: boolean }).__fromServer;
    const hydrationKey = `${callerData.id}:${fromServer ? 'server' : 'cache'}`;
    if (settingsHydratedForCallerRef.current === hydrationKey) return;
    if (settingsHydratedForCallerRef.current === `${callerData.id}:server`) return;
    settingsHydratedForCallerRef.current = hydrationKey;
    // Every hydration (the cached copy, then the server's) is a load, not a
    // change: no save until its setState batch has settled.
    settingsInitRef.current = true;
    const gen = ++hydrationGenRef.current;
    const s = callerData.dialer_settings as any;
    if (s.language) setLanguage(s.language);
    if (s.theme) setTheme(s.theme);
    else if (s.darkMode !== undefined) setTheme(s.darkMode ? 'dark' : 'sober');
    if (s.parallelCallsCount !== undefined) setParallelCallsCount(Number(s.parallelCallsCount));
    if (s.callerIdRotation) setCallerIdRotation(s.callerIdRotation);
    if (s.ringTimeout !== undefined) setRingTimeout(Number(s.ringTimeout));
    if (s.maxCallAttempts) setMaxCallAttempts(s.maxCallAttempts);
    if (s.maxCallAttemptsPeriod) setMaxCallAttemptsPeriod(s.maxCallAttemptsPeriod);
    // The toggle is saved on its own: voicemailMode is 'off' until a message
    // exists, so a toggle switched on before the upload was lost on reload.
    if (s.powerModeLeaveVoicemail !== undefined) setPowerModeLeaveVoicemail(Boolean(s.powerModeLeaveVoicemail));
    else if (s.voicemailMode && (s.voicemailMode === 'off' || s.voicemailMode === 'recorded')) setVoicemailMode(s.voicemailMode);
    if (s.voicemailAudioUrl !== undefined) setVoicemailAudioUrl(s.voicemailAudioUrl);
    if (s.completeTaskOnDial !== undefined) setCompleteTaskOnDial(Boolean(s.completeTaskOnDial));
    if (s.powerMode !== undefined) setPowerMode(Boolean(s.powerMode));
    if (s.powerModePauseBetweenBatches !== undefined) setPowerModePauseBetweenBatches(Number(s.powerModePauseBetweenBatches));
    if (s.navTabOrder) setNavTabOrder(s.navTabOrder);
    if (s.columnConfig) setColumnConfig(s.columnConfig);
    if (s.crmProvider) setCrmProvider(s.crmProvider);
    if (s.hubspotSyncConfig) setHubspotSyncConfig(s.hubspotSyncConfig);
    if (s.selectedMicrophone) setSelectedMicrophone(s.selectedMicrophone);
    if (s.soundEnabled !== undefined) setSoundEnabled(Boolean(s.soundEnabled));
    if (s.powerHourGoal !== undefined) setPowerHourGoal(Number(s.powerHourGoal));
    if (s.stopAfterConnected !== undefined) setStopAfterConnected(Number(s.stopAfterConnected) || 0);
    if (Array.isArray(s.personalCallerIds)) setPersonalCallerIds(s.personalCallerIds);
    if (s.smartQueueEnabled !== undefined) setSmartQueueEnabled(Boolean(s.smartQueueEnabled));
    if (s.hubspotAutoSyncTasks !== undefined) setHubspotAutoSyncTasks(Boolean(s.hubspotAutoSyncTasks));
    if (s.hubspotAutoSyncRecordings !== undefined) setHubspotAutoSyncRecordings(Boolean(s.hubspotAutoSyncRecordings));
    if (s.hubspotLastSyncTasks) setHubspotLastSyncTasks(new Date(s.hubspotLastSyncTasks));
    if (s.hubspotLastSyncRecordings) setHubspotLastSyncRecordings(new Date(s.hubspotLastSyncRecordings));
    if (Array.isArray(s.territories)) setTerritories(s.territories);
    if (Array.isArray(s.activeMarkets)) setActiveMarkets(s.activeMarkets);
    if (s.conversationThreshold !== undefined) setConversationThreshold(Number(s.conversationThreshold));
    if (s.conversationOutcomes !== undefined) setConversationOutcomes(s.conversationOutcomes);
    if (s.powerHourDuration !== undefined) setPowerHourDuration(Number(s.powerHourDuration));
    if (s.remindersEnabled !== undefined) setRemindersEnabled(Boolean(s.remindersEnabled));
    if (s.reminderChannels && typeof s.reminderChannels === 'object') setReminderChannels(s.reminderChannels);
    if (s.aiCoachEnabledPowerMode !== undefined) setAiCoachEnabledPowerMode(Boolean(s.aiCoachEnabledPowerMode));
    if (s.aiCoachEnabledManualCall !== undefined) setAiCoachEnabledManualCall(Boolean(s.aiCoachEnabledManualCall));
    // Delay allowing saves to skip the batch of setState-triggered effects.
    setTimeout(() => { if (hydrationGenRef.current === gen) settingsInitRef.current = false; }, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callerData?.id, (callerData as { __fromServer?: boolean } | null)?.__fromServer]);

  // Debounce-save settings to backend (2s after last change).
  useEffect(() => {
    if (!callerData?.id) return;
    const settings = {
      language, theme, darkMode: theme === 'dark', soundEnabled, parallelCallsCount, callerIdRotation, ringTimeout,
      maxCallAttempts, maxCallAttemptsPeriod, voicemailMode, voicemailAudioUrl, powerModeLeaveVoicemail,
      completeTaskOnDial, powerMode, powerModePauseBetweenBatches, navTabOrder, columnConfig, crmProvider, hubspotSyncConfig,
      selectedMicrophone, powerHourGoal, smartQueueEnabled, stopAfterConnected, personalCallerIds,
      hubspotAutoSyncTasks, hubspotAutoSyncRecordings,
      hubspotLastSyncTasks: hubspotLastSyncTasks ? hubspotLastSyncTasks.toISOString() : null,
      hubspotLastSyncRecordings: hubspotLastSyncRecordings ? hubspotLastSyncRecordings.toISOString() : null,
      territories, activeMarkets, conversationThreshold, conversationOutcomes,
      powerHourDuration, remindersEnabled, reminderChannels,
      aiCoachEnabledPowerMode, aiCoachEnabledManualCall,
    };
    const json = JSON.stringify(settings);
    // While hydrating, the state is what the server holds: remember it.
    if (settingsInitRef.current) { serverSettingsJsonRef.current = json; return; }
    // Nothing changed since the server's copy (a re-render, a change undone
    // within the debounce): no request (QA-W4-1).
    if (json === serverSettingsJsonRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      serverSettingsJsonRef.current = json;
      pushSettings(settings);
      // Keep the cached caller in step, so the next load starts from the
      // settings just saved rather than from the previous session's.
      try {
        const raw = localStorage.getItem('revcall_caller');
        if (raw) {
          const cached = JSON.parse(raw);
          localStorage.setItem('revcall_caller', JSON.stringify({ ...cached, dialer_settings: settings }));
        }
      } catch { /* storage unavailable */ }
    }, 2000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    language, theme, soundEnabled, parallelCallsCount, callerIdRotation, ringTimeout,
    maxCallAttempts, maxCallAttemptsPeriod, voicemailMode, voicemailAudioUrl, powerModeLeaveVoicemail,
    completeTaskOnDial, powerMode, powerModePauseBetweenBatches, navTabOrder, columnConfig, crmProvider, hubspotSyncConfig,
    selectedMicrophone, powerHourGoal, smartQueueEnabled, stopAfterConnected, personalCallerIds,
    hubspotAutoSyncTasks, hubspotAutoSyncRecordings, hubspotLastSyncTasks, hubspotLastSyncRecordings,
    territories, activeMarkets, conversationThreshold, conversationOutcomes,
    powerHourDuration, remindersEnabled, reminderChannels,
    aiCoachEnabledPowerMode, aiCoachEnabledManualCall,
    callerData?.id,
  ]);

  const value: DialerSettingsValue = {
    settingsSave, retrySettingsSave,
    language, setLanguage,
    theme, setTheme, darkMode, cycleTheme,
    soundEnabled, setSoundEnabled,
    powerMode, setPowerMode,
    parallelCallsCount, setParallelCallsCount,
    callerIdRotation, setCallerIdRotation,
    ringTimeout, setRingTimeout,
    maxCallAttempts, setMaxCallAttempts,
    maxCallAttemptsPeriod, setMaxCallAttemptsPeriod,
    powerModePauseBetweenBatches, setPowerModePauseBetweenBatches,
    completeTaskOnDial, setCompleteTaskOnDial,
    powerModeLeaveVoicemail, setPowerModeLeaveVoicemail,
    voicemailAudioUrl, setVoicemailAudioUrl,
    voicemailMode, setVoicemailMode,
    kanbanEnabled, setKanbanEnabled,
    viewMode, setViewMode,
    navTabOrder, setNavTabOrder,
    columnConfig, setColumnConfig,
    selectedMicrophone, setSelectedMicrophone,
    crmProvider, setCrmProvider,
    hubspotSyncConfig, setHubspotSyncConfig,
    hubspotAutoSyncTasks, setHubspotAutoSyncTasks,
    hubspotAutoSyncRecordings, setHubspotAutoSyncRecordings,
    hubspotLastSyncTasks, setHubspotLastSyncTasks,
    hubspotLastSyncRecordings, setHubspotLastSyncRecordings,
    powerHourGoal, setPowerHourGoal,
    stopAfterConnected, setStopAfterConnected,
    personalCallerIds, setPersonalCallerIds,
    powerHourDuration, setPowerHourDuration,
    smartQueueEnabled, setSmartQueueEnabled,
    territories, setTerritories,
    activeMarkets, setActiveMarkets,
    conversationThreshold, setConversationThreshold,
    conversationOutcomes, setConversationOutcomes,
    remindersEnabled, setRemindersEnabled,
    reminderChannels, setReminderChannels,
    aiCoachEnabledPowerMode, setAiCoachEnabledPowerMode,
    aiCoachEnabledManualCall, setAiCoachEnabledManualCall,
  };

  return (
    <DialerSettingsContext.Provider value={value}>
      {children}
    </DialerSettingsContext.Provider>
  );
}

export function useDialerSettings(): DialerSettingsValue {
  const v = useContext(DialerSettingsContext);
  if (!v) throw new Error('useDialerSettings must be used within DialerSettingsProvider');
  return v;
}
